import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../context/AuthContext";
import { db } from "../firebase";
import { HEADERTEXT_TYPES, FB_COLLECTION_PATH } from "../functions/postActionTypes";
import {
  IonButton,
  IonContent,
  IonPage,
  IonGrid,
  IonCol,
  IonRow,
  IonLabel,
  IonAvatar,
  IonIcon,
  IonList,
  IonFab,
  IonFabButton,
  useIonAlert,
  IonItem,
  IonBadge,
  useIonViewWillLeave,
} from "@ionic/react";
import { people, pencilSharp, camera } from "ionicons/icons";
import { Link, Redirect, useHistory } from "react-router-dom";
import {
  query,
  where,
  collectionGroup,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";
import "../theme/AppStyles.css";
import { ProfileContext } from "../context/ProfileContext";
import { WerkerSchedulerHeader } from "../components/WerkHeader";
import { useProfileActions } from "../hooks/useProfileActions";
import { Capacitor } from "@capacitor/core";
import { Purchases } from "@revenuecat/purchases-capacitor";

import TabNavigator from "../components/TabNavigator";
import { LoadingScreen } from "../components/LoadingScreen";
import RedeemInviteCodeModal from "../components/RedeemInviteCodeModal";
import { isDesktop } from "../utils/platformUtils";
import ProfileDesktop from "./desktop/ProfileDesktop";

const Profile: React.FC = () => {
  const { currentUser, authReady } = useContext(AuthContext);
  const { profile, profilePicBadge, buttonColor } = useContext(ProfileContext);
  const history = useHistory();
  const [hasStuff, setHasStuff] = useState(false);
  const [present] = useIonAlert();
  const [showInviteCodeModal, setShowInviteCodeModal] = useState(false);

  const {
    uploadProfilePhoto,
    logoutUser,
    deleteUserAccount,
    isAdmin,
    navigateToAdminView,
    navigateToEditProfile,
    navigateToPaywall,
  } = useProfileActions({
    profile,
    currentUser,
    presentAlert: present,
    isDesktopMode: false,
  });

  const blurFocusedElement = useCallback(() => {
    let activeElement: Element | null = document.activeElement;

    while (activeElement && (activeElement as HTMLElement).shadowRoot?.activeElement) {
      activeElement = (activeElement as HTMLElement).shadowRoot?.activeElement || null;
    }

    if (activeElement instanceof HTMLElement && activeElement !== document.body) {
      activeElement.blur();
    }

    const hostElement = document.activeElement;
    if (hostElement instanceof HTMLElement && hostElement !== document.body) {
      hostElement.blur();
    }
  }, []);

  const navigateToEditProfileWithBlur = useCallback(() => {
    blurFocusedElement();
    navigateToEditProfile();
  }, [blurFocusedElement, navigateToEditProfile]);

  useIonViewWillLeave(() => {
    blurFocusedElement();
  });

  interface UsersData {
    Uid: string;
    FirstName: string;
    LastName: string;
    Email: string;
    Company: string;
    Occupation: string;
    ProfilePicURL: string;
  }
  const [usersListShort, setUsersListShort] = React.useState<UsersData[]>([
    {
      Uid: "0",
      FirstName: "",
      LastName: "",
      Email: "",
      Company: "",
      Occupation: "",
      ProfilePicURL: "../assets/profilePic.png",
    },
  ]);

  useEffect(() => {
    const associatesSlice = Array.isArray(profile.Associates)
      ? profile.Associates.slice(0, 30)
      : [];
    if (associatesSlice.length === 0) return;

    const fetchUsers_ShortList = async () => {
      const userSearch = query(
        collectionGroup(db, FB_COLLECTION_PATH.UsersPublicFacing),
        where("uid", "in", associatesSlice),
        orderBy("firstName", "asc"),
        limit(3)
      );

      try {
        const unsubscribe = onSnapshot(userSearch, (querySnapshot) => {
          if (querySnapshot.docs) {
            const usersMappingArray: any[] = [];
            setUsersListShort(usersMappingArray);
            setHasStuff(false);
            querySnapshot.forEach((doc) => {
              setHasStuff(true);
              const userAttributes = doc.data();
              if (doc.id !== profile.UserId) {
                // create an object that I can push to the array
                const userObject = {
                  Uid: userAttributes.uid,
                  FirstName: userAttributes.firstName,
                  LastName: userAttributes.lastName,
                  Company: userAttributes.company,
                  Occupation: userAttributes.occupation,
                  ProfilePicURL: userAttributes.profilePicURL,
                };
                usersMappingArray.push(userObject);
                setUsersListShort(usersMappingArray);
              }
            });
          } else {
            // console.log("didn't find any records");
          }
        });
        return () => {
          unsubscribe();
        };
      } catch (err) {
        // console.log(err);
      }
    };
    fetchUsers_ShortList();
  }, [profile]);

  function linkToAssociates() {
    history.push("/Associates");
  }

  const ProfileAvatar: React.FC = () => {
    return (
      <React.Fragment>
        <IonRow
          className="profileGrid"
        >
          <IonCol>
            <IonFab
              horizontal="end"
              vertical="bottom"
            >
              <IonFabButton
                color="medium"
                onClick={uploadProfilePhoto}
              >
                <IonIcon
                  icon={camera}
                ></IonIcon>
                {profilePicBadge === 1 && (
                  <IonBadge
                    color="danger"
                    style={{
                      position: "absolute",
                      top: "8px",
                      right: "3px", // Adjust this for positioning
                    }}
                  >
                    {profilePicBadge}
                  </IonBadge>
                )}
              </IonFabButton>
            </IonFab>
            <IonAvatar
              className="avatarino"
            >
              <img src={profile.ProfilePicURL} alt=""></img>
            </IonAvatar>
          </IonCol>
          <IonCol>
            {" "}
          </IonCol>
        </IonRow>
      </React.Fragment>
    );
  };
  const ProfileDetails: React.FC = () => {
    return (
      <React.Fragment>
        <ProfileName />
        <ProfileOccupationAndEmail />
      </React.Fragment>
    );
  };
  const ProfileButtons: React.FC = () => {
    return (
      <React.Fragment>
        <IonRow
          className="profileGrid"
        >
          <IonCol size="8">
            <IonButton
              color={buttonColor}
              expand="block"
              fill="solid"
              onClick={navigateToEditProfileWithBlur}
            >
              <IonIcon
                icon={pencilSharp}
              />
              Edit Profile
            </IonButton>
          </IonCol>
          <IonCol size="3">
            <IonButton
              color="medium"
              expand="block"
              fill="solid"
              onClick={() =>
                present({
                  header: "Logout",
                  buttons: ["Cancel", { text: "Ok", handler: logoutUser }],
                  // onDidDismiss: (e) => console.log("did dismiss"),
                })
              }
            >
              Logout
            </IonButton>
          </IonCol>
        </IonRow>
      </React.Fragment>
    );
  };
  // const DeleteAcctButton: React.FC = () => {
  //   return (
  //     <React.Fragment>
  //       <IonRow>
  //         <IonCol></IonCol>
  //       </IonRow>
  //       <IonRow>
  //         <IonCol></IonCol>
  //       </IonRow>
  //       <IonRow>
  //         <IonCol></IonCol>
  //       </IonRow>
  //       <IonRow>
  //         <IonCol></IonCol>
  //       </IonRow>
  //       <IonRow className="profileGrid">
  //         <IonCol size="3">
  //           <IonButton
  //             color="medium"
  //             // expand="block"
  //             fill="solid"
  //             onClick={() => present({
  //               header: "!!! Delete Account? This is permanent !!!",
  //               buttons: ["Cancel", { text: "Ok", handler: deleteAccount }],
  //               onDidDismiss: (e) => {
  //                 // console.log("did dismiss");
  //               },
  //             })}            >
  //             Delete Account
  //           </IonButton>
  //         </IonCol>
  //       </IonRow>
  //     </React.Fragment>
  //   );
  // };

  // const handlePaywall = async () => {
  //   // Subscribed Schedulers should never see the upsell Paywall.
  //   // Instead, route to the platform's native subscription management UI.
  //   if (profile.SubscriptionType === "Scheduler") {
  //     try {
  //       if (Capacitor.isNativePlatform()) {
  //         await Purchases.showManageSubscriptions();
  //       } else {
  //         // Web has no native store management; surface a friendly hint.
  //         present({
  //           header: "Manage Subscription",
  //           message:
  //             "Subscription management is available in the iOS or Android app via your App Store / Google Play account.",
  //           buttons: ["OK"],
  //         });
  //       }
  //     } catch (err) {
  //       console.error("[Profile] showManageSubscriptions failed:", err);
  //     }
  //     return;
  //   }
  //   navigateToPaywall("Home.Scheduler");
  // };

  const SettingsSection: React.FC = () => (
    <>
      <IonRow>
        <IonCol>
          <h4>Settings:</h4>
        </IonCol>
      </IonRow>
      <IonList>
        {/* Subscription control */}
        <IonItem>
          <IonLabel>
            Subscription:&nbsp;
            <strong>{profile.SubscriptionType}</strong>
          </IonLabel>
          {/* <IonButton
            color="dark"
            slot="end"
            onClick={handlePaywall}
          >
            {profile.SubscriptionType === "Werker" && "Go Pro"}
            {profile.SubscriptionType === "Scheduler" && "Downgrade"}
          </IonButton> */}
        </IonItem>

        {/* Input Invite Code */}
        <IonItem
          button
          onClick={() => setShowInviteCodeModal(true)}
        >
          <IonLabel>
            Input Invite Code
          </IonLabel>
        </IonItem>

        {/* Logout */}
        <IonItem
          button
          onClick={() =>
            present({
              header: "Logout",
              buttons: ["Cancel", { text: "Ok", handler: logoutUser }],
            })
          }
        >
          <IonLabel>
            Logout
          </IonLabel>
        </IonItem>

        <IonItem
          button
          onClick={() =>
            present({
              header: "!!! Delete Account? This is permanent !!!",
              buttons: ["Cancel", { text: "Ok", handler: deleteUserAccount }],
              onDidDismiss: (_e) => {
                // console.log("did dismiss");
              },
            })
          }
        >
          <IonLabel>
            Delete Account
          </IonLabel>
        </IonItem>
        {isAdmin && (
          <IonItem
            button
            onClick={() =>
              present({
                header: "Switch to Admin View?",
                buttons: ["Cancel", { text: "Ok", handler: navigateToAdminView }],
                onDidDismiss: (_e) => {
                  // console.log("did dismiss");
                },
              })
            }
          >
            <IonLabel>
              Switch to Admin View
            </IonLabel>
          </IonItem>
        )}

        {/* future settings go here */}
      </IonList>
    </>
  );

  const _ProfileAssociatesSnapshot: React.FC = () => {
    return (
      <React.Fragment>
        <IonRow>
          <IonCol>
            <h4>Associates</h4>
          </IonCol>
        </IonRow>
        <UsersList />
        <IonRow>
          <IonCol>
            <Link style={{ textDecoration: "none" }} to={"/Associates"}>
              <IonButton
                fill="clear"
                size="small"
                onClick={linkToAssociates}
              >
                <IonIcon
                  size="large"
                  icon={people}
                />
                <IonLabel
                  color="primary"
                >
                  <h4>View All</h4>
                </IonLabel>
              </IonButton>
            </Link>
          </IonCol>
        </IonRow>
      </React.Fragment>
    );
  };
  const ProfileName: React.FC = () => {
    return (
      <React.Fragment>
        <IonRow
          className="profileGrid"
        >
          <IonCol size="">
            <h2>
              {profile.FirstName} {profile.LastName}
            </h2>
          </IonCol>
        </IonRow>
      </React.Fragment>
    );
  };
  const ProfileOccupationAndEmail: React.FC = () => {
    return (
      <React.Fragment>
        <IonRow
          className="profileGrid"
        >
          <IonCol size="11">
            <p>
              {profile.Occupation} @ {profile.Company}
            </p>
            <p>{profile.Email}</p>
          </IonCol>
        </IonRow>
      </React.Fragment>
    );
  };
  const UserBio: React.FC = () => {
    return (
      <React.Fragment>
        <IonRow
          className="profileGrid"
        >
          <IonCol>
            <IonButton
              color="medium"
              size="small"
              onClick={navigateToEditProfileWithBlur}
            >
              <IonIcon
                icon={pencilSharp}
              />
              Edit Bio
            </IonButton>
          </IonCol>
        </IonRow>
        <IonRow
          className="profileGrid"
        >
          <IonCol>
            {profile.UserBio}
          </IonCol>
          <IonCol
            size="1"
          ></IonCol>
        </IonRow>
        <IonRow>
          <IonCol>
            <strong>HINT:</strong> Hashtag "#" areas/Counties that you Werk for schedulers to see it when sending out
            jobs!
          </IonCol>
        </IonRow>
      </React.Fragment>
    );
  };
  const UserNoBio: React.FC = () => {
    return (
      <React.Fragment>
        <IonRow
          className="profileGrid"
        >
          <IonCol>
            <IonButton
              color="medium"
              size="small"
              onClick={navigateToEditProfileWithBlur}
            >
              <IonIcon
                icon={pencilSharp}
              />
              Edit Bio
            </IonButton>
          </IonCol>
        </IonRow>
        <IonRow
          className="profileGrid"
        >
          <IonCol>
            About you... Tell others about yourelf.
          </IonCol>
          <IonCol
            size="1"
          ></IonCol>
        </IonRow>
        <IonRow>
          <IonCol>
            <strong>HINT:</strong> Hashtag "#" areas/Counties that you Werk for schedulers to see it when sending out
            jobs!
          </IonCol>
        </IonRow>
      </React.Fragment>
    );
  };

  const UsersList: React.FC = () => {
    if (hasStuff) {
      return (
        <React.Fragment>
          <IonList>
            {usersListShort.map(
              (user: {
                Uid: React.Key | null | undefined;
                ProfilePicURL: string;
                FirstName: string | null | undefined;
                LastName: string | null | undefined;
                Company: string | null | undefined;
              }) => (
                <Link to={`/AssociateProfile/${user.Uid}`} key={user.Uid} style={{ textDecoration: "none" }}>
                  <IonItem>
                    <IonAvatar
                      className="avatario"
                      slot="start"
                    >
                      <img src={user.ProfilePicURL} alt="" />
                    </IonAvatar>
                    <IonLabel
                      className="labelo"
                    >
                      <h2>
                        {user.FirstName} {user.LastName}
                      </h2>
                      <p>{user.Company}</p>
                    </IonLabel>
                    <br></br>
                  </IonItem>
                </Link>
              )
            )}
          </IonList>
        </React.Fragment>
      );
    } else {
      return (
        <React.Fragment>
          <IonRow>
            <IonCol
            ></IonCol>
          </IonRow>
          <IonRow>
            <IonCol
              size="10"
            >
              None to display - Go make some friends...
            </IonCol>
            <IonCol
            ></IonCol>
          </IonRow>
        </React.Fragment>
      );
    }
  };

  const UserBioElement: React.FC = () => {
    if (profile.UserBio === "") {
      return <UserNoBio />;
    }
    return <UserBio />;
  };

  if (!authReady) {
    return <LoadingScreen LoadingText="Loading" />;
  }

  if (currentUser.uid === "") {
    return <Redirect to="login" />;
  }

  // Desktop mode - render desktop version
  if (isDesktop()) {
    return <ProfileDesktop />;
  }

  return (
    <IonPage>
      <WerkerSchedulerHeader title={HEADERTEXT_TYPES.Profile} />
      <IonContent>
        <br></br>
        <IonGrid>
          <ProfileAvatar />
          <ProfileDetails />
          <ProfileButtons />
          <UserBioElement />
          <br></br>
          {/* <ProfileAssociatesSnapshot /> */}
          {/* <DeleteAcctButton /> */}
          <SettingsSection />
        </IonGrid>
      </IonContent>
      <RedeemInviteCodeModal
        isOpen={showInviteCodeModal}
        onDidDismiss={() => setShowInviteCodeModal(false)}
      />
      <TabNavigator />
    </IonPage>
  );
};
export default Profile;
